interface CognitoTokenSet {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly expiresAtEpochMs: number;
}

export interface CognitoAuthConfig {
  readonly domain: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly scope: string;
}

interface TokenEndpointResponse {
  readonly id_token: string;
  readonly access_token: string;
  readonly refresh_token?: string;
  readonly expires_in?: number;
}

const TOKEN_STORAGE_KEY = "band-sync-lab:cognito:tokens";
const PKCE_VERIFIER_KEY = "band-sync-lab:cognito:pkce-verifier";
const OAUTH_STATE_KEY = "band-sync-lab:cognito:oauth-state";
const POST_LOGIN_REDIRECT_KEY = "band-sync-lab:cognito:post-login-redirect";
const REFRESH_MARGIN_MS = 60_000;

function normalizeDomain(domain: string): string {
  const trimmed = domain.trim().replace(/\/$/, "");
  if (!trimmed) {
    return "";
  }
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function parseJwtExpiryEpochMs(jwt: string): number | undefined {
  const parts = jwt.split(".");
  const payload = parts[1];
  if (!payload) {
    return undefined;
  }
  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = JSON.parse(atob(padded)) as { exp?: unknown };
    if (typeof decoded.exp === "number" && Number.isFinite(decoded.exp)) {
      return decoded.exp * 1000;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function createRandomHex(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function createPkceChallenge(
  verifier: string,
): Promise<{ challenge: string; method: "S256" }> {
  const maybeCrypto = crypto as unknown as { subtle?: { digest?: unknown } };
  // Cognito requires S256 for PKCE. If secure crypto isn't available, fail fast
  // with a clear local error instead of redirecting to a vague Cognito error page.
  if (typeof maybeCrypto.subtle?.digest !== "function") {
    throw new Error(
      "PKCE S256 requires a secure context (HTTPS/localhost with Web Crypto subtle API).",
    );
  }
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const hash = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return {
    challenge: hash.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
    method: "S256",
  };
}

export class CognitoAuthManager {
  private readonly config: CognitoAuthConfig;
  private readonly fetchImpl: typeof fetch;

  constructor(config: CognitoAuthConfig, fetchImpl: typeof fetch = fetch) {
    this.config = {
      domain: normalizeDomain(config.domain),
      clientId: config.clientId.trim(),
      redirectUri: config.redirectUri.trim(),
      scope: config.scope.trim() || "openid email profile",
    };
    // Always invoke through a wrapper so browser-native fetch implementations
    // don't receive an invalid `this` when called as an object method.
    this.fetchImpl = (input, init) => fetchImpl(input, init);
  }

  isConfigured(): boolean {
    return Boolean(this.config.domain && this.config.clientId && this.config.redirectUri);
  }

  async initialize(): Promise<boolean> {
    if (!this.isConfigured()) {
      return false;
    }
    await this.handleAuthRedirectIfPresent();
    const token = await this.getIdToken();
    return typeof token === "string" && token.length > 0;
  }

  async getIdToken(forceRefresh = false): Promise<string | undefined> {
    const existing = this.readStoredTokens();
    if (!existing) {
      return undefined;
    }
    const shouldRefresh =
      forceRefresh || Date.now() + REFRESH_MARGIN_MS >= existing.expiresAtEpochMs;
    if (!shouldRefresh) {
      return existing.idToken;
    }
    if (!existing.refreshToken) {
      this.clearSession();
      return undefined;
    }
    const refreshed = await this.refreshTokenSet(existing.refreshToken);
    this.storeTokens(refreshed);
    return refreshed.idToken;
  }

  async refreshIdToken(): Promise<string | undefined> {
    return this.getIdToken(true);
  }

  clearSession(): void {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  getLogoutUrl(): string | undefined {
    if (!this.isConfigured()) {
      return undefined;
    }
    const logoutUrl = new URL(`${this.config.domain}/logout`);
    logoutUrl.searchParams.set("client_id", this.config.clientId);
    logoutUrl.searchParams.set("logout_uri", this.config.redirectUri);
    return logoutUrl.toString();
  }

  signOut(): void {
    this.clearSession();
    window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    window.sessionStorage.removeItem(OAUTH_STATE_KEY);
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    const logoutUrl = this.getLogoutUrl();
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  }

  async startLogin(): Promise<void> {
    if (!this.isConfigured()) {
      throw new Error("Cognito auth is not configured");
    }
    const verifier = createRandomHex(64);
    const state = createRandomHex(16);
    const { challenge, method } = await createPkceChallenge(verifier);
    const currentUrl = window.location.href;
    window.sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    window.sessionStorage.setItem(OAUTH_STATE_KEY, state);
    window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentUrl);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: this.config.scope,
      code_challenge_method: method,
      code_challenge: challenge,
      state,
    });
    window.location.assign(`${this.config.domain}/oauth2/authorize?${params.toString()}`);
  }

  private async handleAuthRedirectIfPresent(): Promise<void> {
    const url = new URL(window.location.href);
    const oauthError = url.searchParams.get("error");
    if (oauthError) {
      const oauthErrorDescription = url.searchParams.get("error_description");
      this.clearAuthQueryParams(url);
      throw new Error(
        oauthErrorDescription
          ? `Cognito authorize failed: ${oauthErrorDescription}`
          : `Cognito authorize failed: ${oauthError}`,
      );
    }
    const code = url.searchParams.get("code");
    if (!code) {
      return;
    }
    const returnedState = url.searchParams.get("state");
    const expectedState = window.sessionStorage.getItem(OAUTH_STATE_KEY);
    const verifier = window.sessionStorage.getItem(PKCE_VERIFIER_KEY);
    window.sessionStorage.removeItem(OAUTH_STATE_KEY);
    window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    if (!expectedState || !returnedState || expectedState !== returnedState || !verifier) {
      // Stale/duplicate callback URL (for example refresh or interrupted flow):
      // clear callback params and continue signed-out instead of hard-failing init.
      this.clearAuthQueryParams(url);
      window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      return;
    }
    const tokenResponse = await this.exchangeCodeForTokens(code, verifier);
    this.storeTokens({
      idToken: tokenResponse.id_token,
      accessToken: tokenResponse.access_token,
      ...(tokenResponse.refresh_token ? { refreshToken: tokenResponse.refresh_token } : {}),
      expiresAtEpochMs: this.resolveExpiryEpochMs(tokenResponse.id_token, tokenResponse.expires_in),
    });
    const postLoginRedirect = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)?.trim();
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    this.clearAuthQueryParams(url);
    if (postLoginRedirect && postLoginRedirect !== window.location.href) {
      // Restore pre-login URL (including room query params) so link-based joins
      // don't get lost when Cognito callback runs on localhost.
      window.location.assign(postLoginRedirect);
    }
  }

  private clearAuthQueryParams(url: URL): void {
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  private async exchangeCodeForTokens(
    code: string,
    verifier: string,
  ): Promise<TokenEndpointResponse> {
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.config.clientId,
      code,
      redirect_uri: this.config.redirectUri,
      code_verifier: verifier,
    });
    const response = await this.fetchImpl(`${this.config.domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!response.ok) {
      throw new Error(`Cognito token exchange failed with status ${response.status}`);
    }
    return (await response.json()) as TokenEndpointResponse;
  }

  private async refreshTokenSet(refreshToken: string): Promise<CognitoTokenSet> {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: this.config.clientId,
      refresh_token: refreshToken,
    });
    const response = await this.fetchImpl(`${this.config.domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!response.ok) {
      this.clearSession();
      throw new Error(`Cognito token refresh failed with status ${response.status}`);
    }
    const payload = (await response.json()) as TokenEndpointResponse;
    return {
      idToken: payload.id_token,
      accessToken: payload.access_token,
      refreshToken: payload.refresh_token ?? refreshToken,
      expiresAtEpochMs: this.resolveExpiryEpochMs(payload.id_token, payload.expires_in),
    };
  }

  private resolveExpiryEpochMs(
    idToken: string,
    fallbackExpiresInSeconds: number | undefined,
  ): number {
    const jwtExpiry = parseJwtExpiryEpochMs(idToken);
    if (typeof jwtExpiry === "number") {
      return jwtExpiry;
    }
    const expiresIn =
      typeof fallbackExpiresInSeconds === "number" ? fallbackExpiresInSeconds : 3600;
    return Date.now() + expiresIn * 1000;
  }

  private readStoredTokens(): CognitoTokenSet | undefined {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(raw) as CognitoTokenSet;
      if (
        typeof parsed.idToken === "string" &&
        typeof parsed.accessToken === "string" &&
        typeof parsed.expiresAtEpochMs === "number"
      ) {
        return parsed;
      }
    } catch {
      this.clearSession();
      return undefined;
    }
    return undefined;
  }

  private storeTokens(tokens: CognitoTokenSet): void {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  }
}
